import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Target, PieChart, CheckCircle, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function StressInventoryGuide() {
  const navigate = useNavigate();

  const lifeAreas = [
    { area: "Work/Career", questions: ["Feeling overwhelmed by responsibilities?", "Lacking control or autonomy?", "Poor work-life balance?", "Conflict with colleagues?"] },
    { area: "Relationships", questions: ["Communication issues?", "Feeling unsupported?", "Boundary problems?", "Loneliness or isolation?"] },
    { area: "Health", questions: ["Chronic pain or illness?", "Sleep issues?", "Energy problems?", "Concerns about aging?"] },
    { area: "Finances", questions: ["Debt worries?", "Retirement concerns?", "Living beyond means?", "Financial disagreements?"] },
    { area: "Home/Environment", questions: ["Clutter and disorganization?", "Home maintenance stress?", "Living situation unhappy?", "Too many commitments?"] }
  ];

  const actionableSteps = [
    { stress: "Work overload", action: "Delegate one task, say no to one request, or talk to supervisor about prioritization" },
    { stress: "Relationship conflict", action: "Schedule a calm conversation, seek counseling, or set one boundary" },
    { stress: "Financial worry", action: "Make a budget, set up auto-savings, or consult a financial advisor" },
    { stress: "Health concerns", action: "Schedule overdue appointments, start one healthy habit, or research options" },
    { stress: "Time pressure", action: "Eliminate one commitment, batch similar tasks, or wake up 30 minutes earlier" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-red-100 text-red-700 rounded-full text-sm font-semibold mb-4">
            Week 6 • Day 1 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Stress Inventory
          </h1>
          <p className="text-xl text-secondary-600">Know Your Stressors to Manage Them</p>
        </div>

        {/* Hero */}
        <section className="bg-gradient-to-r from-red-500 to-rose-500 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-start gap-4">
            <PieChart className="w-12 h-12 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-2xl mb-2">Today's Exercise</h3>
              <p className="text-red-100 text-lg">
                Draw a circle divided into life areas. Rate stress in each (1-10). Identify your top 3 stressors 
                and one small thing you could do about each. Awareness is the first step to management.
              </p>
            </div>
          </div>
        </section>

        {/* Life Areas Assessment */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">Rate Each Life Area (1-10)</h2>
          <div className="space-y-6">
            {lifeAreas.map((item, index) => (
              <div key={index} className="bg-red-50 rounded-xl p-5">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-lg font-bold text-red-700">{item.area}</h3>
                  <div className="flex gap-1">
                    {[1,2,3,4,5,6,7,8,9,10].map(n => (
                      <div key={n} className={`w-6 h-6 border rounded text-center text-xs flex items-center justify-center ${
                        n <= 3 ? 'border-green-300 text-green-600' : n <= 6 ? 'border-yellow-300 text-yellow-600' : 'border-red-300 text-red-600'
                      }`}>{n}</div>
                    ))}
                  </div>
                </div>
                <ul className="grid md:grid-cols-2 gap-2">
                  {item.questions.map((q, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-secondary-700">
                      <div className="w-4 h-4 border border-red-300 rounded"></div>
                      {q}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Action Steps */}
        <section className="bg-gradient-to-br from-red-50 to-rose-50 rounded-2xl p-8 border-2 border-red-200">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6 flex items-center gap-2">
            <Target className="w-6 h-6 text-red-500" />
            One Small Step for Each Stressor
          </h2>
          <div className="space-y-4">
            {actionableSteps.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-4">
                <h3 className="font-bold text-red-700 mb-1">{item.stress}</h3>
                <p className="text-sm text-secondary-600">{item.action}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default StressInventoryGuide;
